<template>
  <tr
    class=" hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-16 last-child:pr-3 text-sm"
    :class="
      {
        'bg-gray-50 border-gray-100': depth <= 1,
        'bg-gray-200 border-gray-300': depth === 2,
        'bg-gray-300 border-gray-400': depth === 3,
        'bg-gray-400 border-gray-500': depth >= 4
      }
    "
  >
    <td
      class="text-left"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.billNumber') + ': ' + tradeItem.billNumber + ', ' + $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.tradeItemName') + ': ' + tradeItem.name"
    >
      <div
        class="flex items-start space-x-2"
        :style="{ paddingLeft: `${(depth * 20) + 16}px` }"
      >
        <span class="font-semibold text-gray-900">{{ $props.tradeItem.billNumber }}</span>

        <ProvisionalSumBadge
          v-if="!tradeItem.has_children && tradeItem.is_provisional_sum"
        />

        <p class="w-60 text-gray-700">
          {{ tradeItem.name }}
        </p>
      </div>
    </td>

    <td
      class="pr-3"
      :title="showBudgetCommentTooltip ? varianceComment.body : ''"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.budgetAmount') + ': ' + australianCurrency(tradeItem.budget) + (showBudgetCommentTooltip ? ', ' + varianceComment.body : '')"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showBudgetCommentButton"
          type="button"
          class="btn-icon btn-icon-default mr-1 shrink-0"
          :aria-label="varianceComment ? $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editComment') : $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.addComment')"
          @click.stop="editBudgetComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <button
          v-else-if="!!varianceComment?.body"
          type="button"
          class="btn-icon btn-icon-default mr-1 shrink-0"
          @click.stop="showBudgetcomment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ australianCurrency(tradeItem.budget) }}
        </div>
      </div>
    </td>

    <td class="bg-green-100/50">
      <span v-if="showSubcontractSummary">{{ $tcc(tradeItem.childSubcontractSummaryClaim($props.lumpSumCostIdsForClaimPeriod, $props.ratesCostIdsForClaimPeriod)) }}</span>
    </td>

    <td class="bg-green-100/50">
      <span v-if="showSubcontractSummary">{{ $tcc(tradeItem.childSubcontractSummaryCertified($props.lumpSumCostIdsForClaimPeriod, $props.ratesCostIdsForClaimPeriod)) }}</span>
    </td>

    <td
      v-if="!falsey(tradeItemCost?.current_certified) && !typingCertified && canEditCertified"
      :class="{ 'cursor-pointer': canEditCertified }"
      class="bg-green-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.currentCertified') + ': ' + (!falsey(tradeItemCost?.current_certified) ? $tcc(tradeItemCost.current_certified) : '')"
      @click="typeCertified"
    >
      <div class="group flex items-center justify-end">
        <button
          type="button"
          class="btn-icon group-hover:visible btn-icon-red inline-block invisible"
          :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.removeCertified')"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4"
            @click.stop.capture="removeCertified">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
        </button>

        <button
          v-if="canEditCertified"
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
          :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editCertified')"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>
        {{ !falsey(tradeItemCost?.current_certified) ? $tcc(tradeItemCost.current_certified) : '' }}
      </div>
    </td>

    <td
      v-else-if="canEditCertified"
      class="bg-green-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.currentCertified') + ': ' + $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editCertified')"
    >
      <div class="">
        <AppCurrencyInput
          ref="certifiedInput"
          v-model.number="costForm.form.current_certified"
          name="current_certified"
          type="number"
          min="1"
          class="w-24 h-6 ml-auto form-input"
          @blur="stopTypingCertified"
          @keyup.enter="$event.target.blur()"
          @keyup.esc="typingCertified = false"
        />
      </div>
    </td>

    <td
      v-else
      class="bg-green-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.currentCertified') + ': ' + (!falsey(tradeItemCost?.current_certified) ? $tcc(tradeItemCost.current_certified) : '')"
    >
      {{ !falsey(tradeItemCost?.current_certified) ? $tcc(tradeItemCost.current_certified) : '' }}
    </td>

    <td
      class="bg-green-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.previousAmount') + ': ' + (hasValidBudget ? $tcc(tradeItem.previous(claim)) : '')"
    >
      {{ hasValidBudget ? $tcc(tradeItem.previous(claim)) : '' }}
    </td>

    <td
      class="bg-green-100/50 pr-3"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.cumulativeAmount') + ': ' + (hasValidBudget ? $tcc(tradeItem.previous(claim) + (tradeItemCost?.current_certified || 0)) : '')"
    >
      {{ hasValidBudget ? $tcc(tradeItem.previous(claim) + (tradeItemCost?.current_certified || 0)) : '' }}
    </td>

    <td
      v-if="showPercentages && !falsey(tradeItemCost?.current_certified) && !typingCertified && canEditCertified"
      :class="{ 'cursor-pointer': canEditCertified }"
      class="bg-blue-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.certifiedPercentage') + ': ' + (!falsey(tradeItemCost?.current_certified) ? `${$tn((tradeItemCost.current_certified / tradeItem.budget) * 100)}%` : '')"
      @click="typeCertifiedPercent"
    >
      <div class="group flex items-center justify-end">
        <button
          type="button"
          class="btn-icon group-hover:visible btn-icon-red inline-block invisible"
          :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.removeCertified')"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4"
            @click.stop.capture="removeCertified">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
        </button>

        <button
          v-if="canEditCertified"
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
          :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editCertified')"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        {{ hasValidBudget && !falsey(tradeItemCost?.current_certified) ? `${$tn((tradeItemCost.current_certified / tradeItem.budget) * 100)}%` : '' }}
      </div>
    </td>

    <td
      v-else-if="showPercentages && canEditCertified"
      class="bg-blue-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.certifiedPercentage') + ': ' + $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editCertified')"
    >
      <div class="">
        <AppInput
          ref="certifiedPercentInput"
          v-model.number="currentCertifiedPercent"
          name="current_certified_percent"
          type="number"
          class="w-20 h-6 ml-auto form-input"
          @blur="stopTypingCertified"
          @keyup.enter="$event.target.blur()"
          @keyup.esc="typingCertified = false"
        />
      </div>
    </td>

    <td
      v-else-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.certifiedPercentage') + ': ' + (hasValidBudget && !falsey(tradeItemCost?.current_certified) ? `${$tn((tradeItemCost.current_certified / tradeItem.budget) * 100)}%` : '')"
    >
      {{ hasValidBudget && !falsey(tradeItemCost?.current_certified) ? `${$tn((tradeItemCost.current_certified / tradeItem.budget) * 100)}%` : '' }}
    </td>

    <td
      v-else
      class="bg-blue-100/50"
    />

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.previousPercentage') + ': ' + (hasValidBudget ? $tn((previousAmount / tradeItem.budget) * 100) + '%' : '')"
    >
      {{ hasValidBudget ? $tp((previousAmount / tradeItem.budget) * 100) : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.cumulativePercentage') + ': ' + (hasValidBudget ? $tp(((previousAmount + (tradeItemCost?.current_certified || 0)) / tradeItem.budget) * 100) : '')"
    >
      {{ hasValidBudget ? $tp(((previousAmount + (tradeItemCost?.current_certified || 0)) / tradeItem.budget) * 100) : '' }}
    </td>

    <td
      class="pr-3"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.remainingAmount') + ': ' + (hasValidBudget ? $tcc(Math.abs(tradeItem.budget - previousAmount - (tradeItemCost?.current_certified || 0))) : '')"
    >
      {{ hasValidBudget ? $tcc(Math.max(tradeItem.budget - previousAmount - (tradeItemCost?.current_certified || 0), 0)) : '' }}
    </td>
  </tr>
</template>

<script setup lang="ts">
import ProvisionalSumBadge from '@/components/shared/ProvisionalSumBadge.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, falsey, isNumber, roundDecimals } from '@/helpers';
import { t } from '@/i18n';
import Claim, { ClaimStatus } from '@/models/Claim';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useProjectsStore } from '@/store/projects';
import { useTradeItemCostsStore } from '@/store/tradeItemCosts';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useUserStore } from '@/store/user';
import Swal from 'sweetalert2';
import { nextTick, ref, watch } from 'vue';
import { computed } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  trade: Trade;
  claim: Claim;
  tradeItem: TradeItem;
  depth?: number;
  showPercentages?: boolean;
  showSubcontractSummary?: boolean;
  lumpSumCostIdsForClaimPeriod: Id[];
  ratesCostIdsForClaimPeriod: Id[];
}>();

const tradeItemCostsStore = useTradeItemCostsStore();
const projectsStore = useProjectsStore();
const userStore = useUserStore();
const isReadOnly = userStore.isClientManager();

const tradeItemCost = computed(() => {
  return tradeItemCostsStore.models.with('comments')
    .where('trade_item_id', props.tradeItem.id)
    .where('claim_id', props.claim.id)
    .first();
});

const hasValidBudget = computed(() => props.tradeItem.budget >= 0);

const canEditCertified = computed(() => {
  return !isReadOnly && props.claim.status === ClaimStatus.draft && hasValidBudget.value;
});

const typingCertified = ref(falsey(tradeItemCost.value?.current_certified) && hasValidBudget.value);
const certifiedInput = ref();
const certifiedPercentInput = ref();

const typeCertified = () => {
  if(canEditCertified.value) {
    typingCertified.value = true;

    nextTick(() => {
      certifiedInput.value?.focusInput();
    });
  }
};

const typeCertifiedPercent = () => {
  if(canEditCertified.value) {
    typingCertified.value = true;

    nextTick(() => {
      certifiedPercentInput.value?.focusInput();
    });
  }
};

const costForm = useFormData({
  current_certified: undefined,
  current_claim: undefined,
  comment: undefined,
});

const subcontractSummaryCertified = computed(() => {
  return props.tradeItem.childSubcontractSummaryCertified(
    props.lumpSumCostIdsForClaimPeriod,
    props.ratesCostIdsForClaimPeriod,
  );
});

const previousAmount = computed(() => {
  return props.tradeItem.previous(props.claim);
});

const currentCertifiedPercent = computed({
  get() {
    return roundDecimals((costForm.form.current_certified / props.tradeItem.budget) * 100) || undefined;
  },

  set(value) {
    if(value + roundDecimals(previousAmount.value / props.tradeItem.budget * 100) === 100) {
      costForm.form.current_certified = props.tradeItem.budget - previousAmount.value;

      return;
    }

    costForm.form.current_certified = roundDecimals((value / 100) * props.tradeItem.budget);
  },
});

watch(
  [tradeItemCost, subcontractSummaryCertified],
  ([newValue]) => {
    if(newValue) {
      costForm.setData(newValue);

      return;
    }

    if(falsey(costForm.form.current_certified) && subcontractSummaryCertified.value !== 0) {
      costForm.form.current_certified = subcontractSummaryCertified.value;
    }
  },
  { immediate: true },
);

const storeTradeItemCostAction = useStoreApiAction(tradeItemCostsStore.createTradeItemCost);
const updateTradeItemCostAction = useStoreApiAction(tradeItemCostsStore.updateTradeItemCost);
const updateCommentAction = useStoreApiAction(tradeItemCostsStore.updateComment);
const deleteCommentAction = useStoreApiAction(tradeItemCostsStore.deleteComment);
const deleteTradeItemCostAction = useStoreApiAction(tradeItemCostsStore.deleteTradeItemCost);

const varianceComment = computed(() => {
  return tradeItemCost.value?.comments[0];
});

const showBudgetCommentButton = computed(() => {
  return canEditCertified.value &&
    !falsey(tradeItemCost.value) &&
    !falsey(tradeItemCost.value.current_certified);
});

const showBudgetCommentTooltip = computed(() => {
  return !!varianceComment.value?.body && showBudgetCommentButton.value;
});

const showBudgetcomment = () => {
  Swal.fire({
    icon: 'info',
    title: t('HeadContract.ProgressClaimsLite.TradeRowItem.dialog.commentTitle'),
    text: varianceComment.value?.body,
  });
};

const editBudgetComment = () => {
  Swal.fire({
    icon: 'info',
    title: t('HeadContract.ProgressClaimsLite.TradeRowItem.dialog.commentTitle'),
    text: t('HeadContract.ProgressClaimsLite.TradeRowItem.dialog.commentText'),
    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: varianceComment.value ? 'Update' : 'Save',
    showDenyButton: !!varianceComment.value,
    denyButtonText: 'Delete',
    denyButtonColor: '#d33',
  }).then((result) => {
    if(result.isDenied && varianceComment.value) {
      deleteCommentAction.request(tradeItemCost.value.id, varianceComment.value.id).then(() => {
        toast.success('Comment deleted');
      });
    } else if(result.isConfirmed && result.value) {
      if(varianceComment.value) {
        updateCommentAction.request(tradeItemCost.value.id, varianceComment.value.id, { comment: result.value }).then(
          () => {
            toast.success('Comment updated');
          },
        );
      } else {
        updateTradeItemCostAction.request(tradeItemCost.value.id, { comment: result.value }).then(
          () => {
            toast.success('Comment saved');
          },
        );
      }
    }
  });
};

const stopTypingCertified = async () => {
  if(
    (falsey(costForm.form.current_certified) && !tradeItemCost.value) ||
    !typingCertified.value
  ) {
    return;
  }

  if(!hasValidBudget.value && !falsey(costForm.form.current_certified)) {
    Swal.fire({
      icon: 'error',
      title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidAmount'),
      text: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.negativeBudget'),
    });

    costForm.resetData(['current_certified']);
    typingCertified.value = false;

    return;
  }

  if(roundDecimals(costForm.form.current_certified + previousAmount.value) > props.tradeItem.budget) {
    const budgetDifference = roundDecimals(costForm.form.current_certified + previousAmount.value) -
      props.tradeItem.budget;

    const allowOverBudget = projectsStore.project?.head_contract_settings?.allow_certified_over_budget;

    if(allowOverBudget) {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Total amount exceeds budget.',
        text:
          `The total amount is greater than the budget. It is recommended that you create a variation to capture the remaining overflow ${
            australianCurrency(budgetDifference)
          }. Press OK to save the amount as is.`,

        showCancelButton: true,
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
      });

      if(!result.isConfirmed) {
        return;
      }
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Invalid amount.',
        text:
          `The certified amount cannot be greater than the budget. It is recommended that you create a variation to capture the remaining overflow ${
            australianCurrency(budgetDifference)
          }`,
      });

      costForm.form.current_certified = tradeItemCost.value?.current_certified;

      return;
    }
  }

  if(falsey(costForm.form.current_certified) && tradeItemCost.value && falsey(tradeItemCost.value.current_claim)) {
    deleteTradeItemCostAction.request(tradeItemCost.value.id).then(() => {
      typingCertified.value = false;
    }).catch((_error) => {
      Swal.fire({
        icon: 'error',
        title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.somethingWentWrong'),
      });

      typingCertified.value = true;
    });
  } else if(tradeItemCost.value) {
    costForm.resetData(['comment']);

    // if current claim is set and incoming certified is different, require comment
    if(
      !falsey(tradeItemCost.value.current_claim) &&
      costForm.form.current_certified !== tradeItemCost.value.current_claim
    ) {
      // const comment = !tradeComment ?
      const comment = await Swal.fire({
        icon: 'warning',
        title: t('HeadContract.ProgressClaimsLite.TradeRowItem.dialog.reasonForAdjustment'),
        text: t('HeadContract.ProgressClaimsLite.TradeRowItem.dialog.varianceReasonText'),
        input: 'textarea',
        inputValue: varianceComment.value?.body ?? '',
      });
      // :
      // undefined;

      costForm.form.comment = comment?.value;

      // TODO: 10/27/23 maybe stop prompting to apply to trade

      // if(!tradeComment) {
      //   await Swal.fire({
      //     icon: 'question',
      //     title: 'Apply Comment to Trade?',
      //     showConfirmButton: true,
      //     confirmButtonText: 'Yes',
      //     showDenyButton: true,
      //   }).then((result) => {
      //     if(result.isConfirmed) {
      //       costForm.form.comment_trade_item = true;
      //     }
      //   });
      // }
    }

    updateTradeItemCostAction.request(tradeItemCost.value.id, {
      current_certified: costForm.form.current_certified,
      comment: costForm.form.comment,
    })
      .then((_tradeItemCost) => {
        if(falsey(tradeItemCost.value?.current_certified)) {
          typingCertified.value = true;
        } else {
          typingCertified.value = false;
        }
      }).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            icon: 'error',
            title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidData'),
            text: error.data.message,
          });
        }

        costForm.form.current_certified = tradeItemCost.value.current_certified;

        typingCertified.value = true;
      });
  } else {
    storeTradeItemCostAction.request(props.claim.id, props.tradeItem.id, costForm.form).then((tradeItemCost) => {
      typingCertified.value = false;
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidAmount'),
          text: error.data.message,
        });
      }

      typingCertified.value = true;
    });
  }
};

const deleteCost = () => {
  deleteTradeItemCostAction.request(tradeItemCost.value.id).then(() => {
    costForm.form.current_certified = undefined;
    costForm.form.current_claim = undefined;
    typingCertified.value = true;
  }).catch((_error) => {
    Swal.fire({
      icon: 'error',
      title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.somethingWentWrong'),
    });
  });
};

const removeCertified = () => {
  if(falsey(tradeItemCost.value.current_claim)) {
    deleteCost();
  } else {
    updateTradeItemCostAction.request(tradeItemCost.value.id, { current_certified: undefined })
      .then((tradeItemCost) => {
        console.log(tradeItemCost);
        typingCertified.value = true;
      }).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            icon: 'error',
            title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidAmount'),
            text: error.data.message,
          });
        }

        costForm.form.current_certified = tradeItemCost.value.current_certified;
      });
  }
};
</script>

<style scoped></style>
